import Button, { ButtonProps } from 'components/UI/V3/Button/Button';
import { Dropdown, DropdownContent, DropdownTrigger } from 'components/UI/V3/Dropdown';
import { DropdownContentProps } from 'components/UI/V3/Dropdown/Content/DropdownContent';
import { DropdownFooter } from 'components/UI/V3/Dropdown/Footer/DropdownFooter';
import { DropdownItem } from 'components/UI/V3/Dropdown/Item/DropdownItem';
import React from 'react';
import styles from './SelectMultipleDropdown.module.scss';
import { useLayoutEffect, useRef, useState } from 'react';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';

export type SelectDropdownOption<T> = {
    id: T;
    label: string;
};

type SelectMultipleDropdownProps<T> = {
    label?: string;
    showSelectAll?: boolean;
    options: Array<SelectDropdownOption<T>>;

    trigger?: React.ReactNode;

    buttonProps?: Partial<ButtonProps>;

    dropdownProps?: Partial<DropdownContentProps>;

    // multiple-only API
    value: SelectDropdownOption<T>[];
    onChange?: (value: SelectDropdownOption<T>[]) => void;
};

export const SelectMultipleDropdown = <T,>({
    label,
    options,
    showSelectAll,
    value,
    trigger,
    buttonProps,
    dropdownProps,
    onChange
}: SelectMultipleDropdownProps<T>) => {
    const safeOptions = Array.isArray(options) ? options : [];

    const [isOpen, setIsOpen] = React.useState(false);
    // Initialize internal selection from controlled `value`
    const [selectedOptions, setSelectedOptions] = React.useState<Array<SelectDropdownOption<T>>>(() => {
        return Array.isArray(value) ? value : [];
    });

    React.useEffect(() => {
        if (Array.isArray(value)) {
            setSelectedOptions(value);
        }
    }, [value]);

    const isAllSelected = React.useMemo(() => {
        return selectedOptions.length === safeOptions.length && safeOptions.length > 0;
    }, [selectedOptions, safeOptions]);

    const buttonLabel = React.useMemo(() => {
        if (!Array.isArray(value)) return label;
        if (selectedOptions.length === safeOptions.length) return `All ${label}`;
        return `(${selectedOptions.length}) ${label}`;
    }, [selectedOptions, label, safeOptions.length, value]);

    const measureRef = useRef<HTMLDivElement | null>(null);
    const [measuredWidth, setMeasuredWidth] = useState<number | undefined>(undefined);

    useLayoutEffect(() => {
        const measure = () => {
            if (measureRef.current) {
                const rect = measureRef.current.getBoundingClientRect();
                setMeasuredWidth(Math.round(rect.width));
            }
        };

        // Measure immediately after paint
        measure();

        // Use ResizeObserver when available to update measurement dynamically
        let ro: ResizeObserver | null = null;
        if (typeof ResizeObserver !== 'undefined' && measureRef.current) {
            ro = new ResizeObserver(() => measure());
            ro.observe(measureRef.current);
        } else {
            // Fallback to window resize
            window.addEventListener('resize', measure);
        }

        return () => {
            if (ro && measureRef.current) ro.disconnect();
            else window.removeEventListener('resize', measure);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [buttonLabel, buttonProps?.fullWidth]);

    const triggerElement = React.useMemo(() => {
        const effectiveButtonProps = {
            ...(buttonProps || {}),
            fullWidth: buttonProps?.fullWidth ?? true,
            variant: buttonProps?.variant ?? 'outlined'
        } as Partial<ButtonProps>;

        const btn = (
            <Button
                label={buttonLabel}
                rightIcon={faChevronDown as any}
                {...effectiveButtonProps}
                className={styles.triggerButton}
            />
        );

        if (trigger) return trigger;

        // Wrap button in a measuring element so we can set minWidth on the dropdown
        return (
            <div
                ref={measureRef}
                className={styles.triggerWrapper}
            >
                {btn}
            </div>
        );
    }, [selectedOptions, buttonLabel, buttonProps, trigger]);

    const isOptionSelected = (option: SelectDropdownOption<T>): boolean => {
        return selectedOptions.some(selected => selected.id === option.id);
    };

    const renderOptionItem = (option: SelectDropdownOption<T>) => {
        const isSelected = isOptionSelected(option);

        return (
            <DropdownItem
                key={String(option.id)}
                checked={isSelected}
                type={'checkbox'}
                onSelect={(checked: boolean) => optionSelectHandler(option, checked)}
            >
                {option.label}
            </DropdownItem>
        );
    };

    const selectAllHandler = (checked: boolean) => {
        if (checked) {
            setSelectedOptions(safeOptions);
        } else {
            setSelectedOptions([]);
        }
    };

    const optionSelectHandler = (newValue: SelectDropdownOption<T>, checked: boolean) => {
        const newSelection = checked
            ? [...selectedOptions, newValue]
            : selectedOptions.filter(v => v.id !== newValue.id);

        setSelectedOptions(newSelection);
    };

    const applyHandler = () => {
        setIsOpen(false);
        onChange?.(selectedOptions);
    };

    return (
        <Dropdown
            open={isOpen}
            onOpenChange={setIsOpen}
        >
            <DropdownTrigger>{triggerElement}</DropdownTrigger>

            <DropdownContent
                {...(dropdownProps || {})}
                minWidth={
                    dropdownProps?.minWidth ??
                    measuredWidth ??
                    (measureRef.current ? Math.round(measureRef.current.getBoundingClientRect().width) : 240)
                }
                side={dropdownProps?.side ?? 'bottom'}
                align={dropdownProps?.align ?? 'start'}
                className={`${dropdownProps?.className ?? ''} ${styles.dropdownContentFix} ${
                    measuredWidth ? '' : styles.dropdownNotMeasured
                }`.trim()}
            >
                {showSelectAll && safeOptions.length > 0 && (
                    <DropdownItem
                        type="checkbox"
                        checked={isAllSelected}
                        onSelect={selectAllHandler}
                    >
                        All {label}
                    </DropdownItem>
                )}

                {safeOptions.map(renderOptionItem)}

                <DropdownFooter>
                    <Button
                        label="Apply"
                        variant="primary"
                        disabled={selectedOptions.length === 0}
                        onClick={applyHandler}
                    />
                </DropdownFooter>
            </DropdownContent>
        </Dropdown>
    );
};

export default SelectMultipleDropdown;
